/**
 * System Performance Dashboard
 * CPU/Memory/Disk usage, API/DB response times, downtime incidents, latency, microservice health
 */

import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { cn } from '../../lib/utils';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  MdMemory,
  MdStorage,
  MdSpeed,
  MdWarning,
  MdCheckCircle,
  MdError,
  MdCloud,
  MdList,
} from 'react-icons/md';

interface ServiceHealth {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  uptime: number | string;
  responseTime: number;
  lastCheck: string;
}

interface SystemMetrics {
  cpuUsage: number;
  memoryUsage: number;
  uptime: number;
  totalMem: number;
  freeMem: number;
  loadAvg: number[];
  diskUsage?: number;
  disk?: {
    used: number;
    total: number;
  };
  avgResponseTime?: number;
  apiThroughput?: number;
  services?: {
    [key: string]: {
      status: string;
      uptime: number;
      responseTime: number;
      lastCheck: string;
    }
  };
}

interface ApiStat {
  endpoint: string;
  count: number;
  avgTime: number;
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  [key: string]: any;
}

export function SystemPerformanceDashboard() {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [microserviceHealth, setMicroserviceHealth] = useState<ServiceHealth[]>([]);
  const [apiStats, setApiStats] = useState<ApiStat[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [metricsRes, statsRes, logsRes] = await Promise.all([
          apiFetch('/api/v1/tech/system/metrics'),
          apiFetch('/api/v1/tech/system/api-stats'),
          apiFetch('/api/v1/tech/system/logs?lines=50')
        ]);

        if (metricsRes.ok) {
          const mData = await metricsRes.json();
          const metricsData = mData.data;
          setMetrics(metricsData);

          // Map services from backend to microserviceHealth state
          if (metricsData && metricsData.services && typeof metricsData.services === 'object') {
            const mappedHealth: ServiceHealth[] = Object.entries(metricsData.services).map(([key, v]) => {
              const value = v as any;
              return {
                service: key.toUpperCase(),
                status: value.status as any,
                uptime: typeof value.uptime === 'number'
                  ? `${Math.floor(value.uptime / 3600)}h ${Math.floor((value.uptime % 3600) / 60)}m`
                  : '100',
                responseTime: value.responseTime,
                lastCheck: new Date(value.lastCheck).toLocaleTimeString()
              };
            });
            setMicroserviceHealth(mappedHealth);
          }

          if (metricsData) {
            setHistory(prev => {
              const newHistory = [...prev, {
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
                cpu: Math.round(metricsData.cpuUsage || 0),
                memory: Math.round(metricsData.memoryUsage || 0)
              }];
              return newHistory.slice(-20); // Keep last 20 data points
            });
          }
        }

        if (statsRes.ok) {
          const sData = await statsRes.json();
          setApiStats(Array.isArray(sData?.data) ? sData.data : []);
        }

        if (logsRes.ok) {
          const lData = await logsRes.json();
          setLogs(Array.isArray(lData?.data) ? lData.data : []);
        }
      } catch (err) {
        console.error('Failed to fetch system monitoring data', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000); // Poll every 5s
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20';
      case 'degraded':
        return 'text-orange-600 bg-orange-50 dark:bg-orange-950/20';
      case 'down':
        return 'text-red-600 bg-red-50 dark:bg-red-950/20';
      default:
        return 'text-[hsl(var(--muted-foreground))] bg-[hsl(var(--secondary))]';
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm hover:border-[hsl(var(--primary))] transition-colors duration-300">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">CPU Usage</p>
            <MdSpeed className="w-5 h-5 text-[#0088cc]" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {metrics ? `${Math.round(metrics.cpuUsage)}%` : '...'}
          </p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">
            {metrics ? `${metrics.loadAvg?.[0]?.toFixed(2) || '0.00'} Load Avg` : 'Loading...'}
          </p>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm hover:border-[hsl(var(--primary))] transition-colors duration-300">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Memory Usage</p>
            <MdMemory className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {metrics ? `${Math.round(metrics.memoryUsage)}%` : '...'}
          </p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">
            {metrics ? `${metrics.freeMem.toFixed(1)}GB free of ${metrics.totalMem.toFixed(1)}GB` : 'Loading...'}
          </p>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm hover:border-[hsl(var(--primary))] transition-colors duration-300">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Disk Usage</p>
            <MdStorage className="w-5 h-5 text-[#0088cc]" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {metrics?.diskUsage !== undefined ? `${Math.round(metrics.diskUsage)}%` : '...'}
          </p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">
            {metrics?.disk ? `${metrics.disk.used.toFixed(1)}GB used of ${metrics.disk.total.toFixed(1)}GB` : 'Loading...'}
          </p>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm hover:border-[hsl(var(--primary))] transition-colors duration-300">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Uptime</p>
            <MdStorage className="w-5 h-5 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {metrics ? `${Math.floor(metrics.uptime / 3600)}h ${Math.floor((metrics.uptime % 3600) / 60)}m` : '...'}
          </p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">Process Uptime</p>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm hover:border-[hsl(var(--primary))] transition-colors duration-300">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">API throughput</p>
            <MdSpeed className="w-5 h-5 text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {metrics?.apiThroughput ?? (Array.isArray(apiStats) ? apiStats.reduce((acc, curr) => acc + curr.count, 0) : 0)}
          </p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">Total requests served</p>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm hover:border-[hsl(var(--primary))] transition-colors duration-300">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Avg Response Time</p>
            <MdSpeed className="w-5 h-5 text-rose-600" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {metrics?.avgResponseTime !== undefined ? `${metrics.avgResponseTime.toFixed(2)}ms` : '...'}
          </p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">Across all APIs</p>
        </div>
      </div>

      {/* Resource Usage Trends */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Resource Usage Trends</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">CPU and Memory usage live updates</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={history}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="time" stroke="#6b7280" />
            <YAxis stroke="#6b7280" domain={[0, 100]} />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="cpu" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="CPU %" animationDuration={300} />
            <Area type="monotone" dataKey="memory" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} name="Memory %" animationDuration={300} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* API Stats */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">API Endpoint Statistics</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Real-time usage and response times</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">
                <th className="pb-3 font-medium">Endpoint</th>
                <th className="pb-3 font-medium">Calls</th>
                <th className="pb-3 font-medium">Avg Latency</th>
                <th className="pb-3 font-medium">Health</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--border))]">
              {apiStats.slice(0, 10).map((stat, index) => (
                <tr key={index} className="hover:bg-[hsl(var(--secondary))] transition-colors">
                  <td className="py-3 font-mono text-xs">{stat.endpoint}</td>
                  <td className="py-3 font-bold">{stat.count}</td>
                  <td className="py-3">{stat.avgTime.toFixed(2)}ms</td>
                  <td className="py-3">
                    <span className={cn(
                      "inline-block w-2 h-2 rounded-full",
                      stat.avgTime < 200 ? "bg-emerald-500" : stat.avgTime < 500 ? "bg-orange-500" : "bg-red-500"
                    )} />
                  </td>
                </tr>
              ))}
              {apiStats.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-[hsl(var(--muted-foreground))] italic">No API calls recorded yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Consol Log */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MdList className="w-6 h-6 text-[#0088cc]" />
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))]">Consol Log</h3>
          </div>
          <span className="text-xs text-[hsl(var(--muted-foreground))]">Auto-refreshing...</span>
        </div>
        <div className="bg-slate-950 rounded-lg p-5 h-[500px] overflow-y-auto font-mono text-[11px] space-y-1.5 border border-[#1e293b] shadow-inner scrollbar-thin">
          {logs.map((log, index) => (
            <div key={index} className="flex flex-col gap-0.5 group hover:bg-white/5 p-1 rounded transition-colors">
              <div className="flex gap-4">
                <span className="text-[hsl(var(--muted-foreground))] shrink-0 select-none">
                  {log.timestamp ? new Date(log.timestamp).toLocaleTimeString([], { hour12: false }) : '--:--:--'}
                </span>
                <span className={cn(
                  "font-bold shrink-0 uppercase w-14 tracking-tighter select-none",
                  log.level === 'error' ? 'text-red-500' : 
                  log.level === 'warn' ? 'text-yellow-500' : 
                  log.level === 'debug' ? 'text-blue-400' : 'text-emerald-500'
                )}>
                  {`[${(log.level || 'info').padEnd(5)}]`}
                </span>
                <span className={cn(
                  "text-[#0088cc]/45 break-all",
                  log.level === 'error' && 'text-red-200'
                )}>
                  {log.message || (typeof log === 'string' ? log : JSON.stringify(log))}
                </span>
              </div>
              {log.error && (
                <div className="ml-24 text-red-500/70 whitespace-pre-wrap text-[10px] italic">
                  {log.error}
                </div>
              )}
              {log.stack && (
                <div className="ml-24 text-red-400/50 whitespace-pre-wrap text-[9px] font-light border-l border-red-900/30 pl-2">
                  {log.stack}
                </div>
              )}
            </div>
          ))}
          {logs.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-[hsl(var(--muted-foreground))] space-y-3">
              <MdList className="w-12 h-12 opacity-20" />
              <p className="italic">Waiting for console output...</p>
            </div>
          )}
        </div>
      </div>

      {/* Microservice Health Check Reports */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Microservice Health Check Reports</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Service status and performance metrics</p>
          </div>
          <MdCloud className="w-6 h-6 text-[#0088cc]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {microserviceHealth.map((service, index) => (
            <div key={index} className="p-4 rounded-lg bg-[hsl(var(--secondary))]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[hsl(var(--foreground))]">{service.service}</h4>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(service.status)}`}>
                  {service.status}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Uptime:</span>
                  <span className="font-medium text-[hsl(var(--foreground))]">{service.uptime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Response Time:</span>
                  <span className="font-medium text-[hsl(var(--foreground))]">{service.responseTime}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Last Check:</span>
                  <span className="font-medium text-[hsl(var(--foreground))] text-xs">{service.lastCheck}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}




